/**
 * Centralized Route Configuration System
 *
 * This file serves as the single source of truth for all application routes.
 * It provides type-safe route building with compile-time parameter validation.
 */

// ============================================================================
// Route Keys Enum
// ============================================================================

export enum AppRoutes {
  // Static routes
  HOME = 'home',
  LOGOUT = 'logout',
  SIGNIN = 'signIn',
  IDENTITY_AUTH = 'identityAuth',
  UNAUTHORIZED = 'unauthorized',
  RESET_PASSWORD = 'resetPassword',
  USER_ONBOARD = 'userOnboard',
  CLIENT_ONBOARD = 'clientOnboard',
  MEMBER_ONBOARD = 'memberOnboard',
  APPLICATIONS = 'applications',
  MARYLAND_SHERPA = 'marylandSherpa',
  LOTTERY_NOMINEE = 'lotteryNominee',
  MKE_ERA_PUBLIC = 'mkeEraPublic',
  STP_DEMO = 'stpDemo',

  // Index routes - Dashboard
  APP_COMPANY = 'appCompany',
  APP_DASHBOARD = 'appDashboard',
  APP_TASKS = 'appTasks',
  APP_TASK_DETAIL = 'appTaskDetail',
  APP_DELIVERABLES = 'appDeliverables',
  APP_DELIVERABLE_DETAIL = 'appDeliverableDetail',
  APP_MILESTONES = 'appMilestones',
  APP_MILESTONE_DETAIL = 'appMilestoneDetail',
  APP_APPROVALS = 'appApprovals',
  APP_APPROVAL_DETAIL = 'appApprovalDetail',

  // Sherpa routes
  SHERPA_ROOT = 'sherpaRoot',
  SHERPA_DASHBOARD = 'sherpaDashboard',
  SHERPA_PROJECTS = 'sherpaProjects',
  SHERPA_PROJECTS_NEW = 'sherpaProjectsNew',
  SHERPA_PROJECTS_IMPORT = 'sherpaProjectsImport',
  SHERPA_PROJECTS_REPORTS = 'sherpaProjectsReports',
  SHERPA_PROJECTS_REPORT_DETAIL = 'sherpaProjectsReportDetail',
  SHERPA_PROJECTS_REPORT_SETTINGS = 'sherpaProjectsReportSettings',
  SHERPA_PROJECT_DETAIL = 'sherpaProjectDetail',
  SHERPA_PROJECT_REPORTS = 'sherpaProjectReports',
  SHERPA_PROJECT_REPORT_DETAIL = 'sherpaProjectReportDetail',
  SHERPA_PROJECT_REPORT_SETTINGS = 'sherpaProjectReportSettings',
  SHERPA_PROJECT_AUDIT = 'sherpaProjectAudit',
  SHERPA_PROJECT_IDMS = 'sherpaProjectIdms',
  SHERPA_PROJECT_IDMS_DRAW = 'sherpaProjectIdmsDraw',
  SHERPA_PROJECT_IDMS_DRAW_DOCUMENT = 'sherpaProjectIdmsDrawDocument',
  SHERPA_PROJECT_IDMS_DRAW_DOCUMENT_DETAIL = 'sherpaProjectIdmsDrawDocumentDetail',
  SHERPA_PROJECT_IDMS_DRAW_INVOICE = 'sherpaProjectIdmsDrawInvoice',
  SHERPA_PROJECT_CHANGE_ORDER = 'sherpaProjectChangeOrder',
  SHERPA_PROJECT_CHANGE_ORDER_BUDGET = 'sherpaProjectChangeOrderBudget',
  SHERPA_PROJECT_CHANGE_ORDER_MILESTONES = 'sherpaProjectChangeOrderMilestones',
  SHERPA_PROJECT_CHANGE_ORDER_DELIVERABLES = 'sherpaProjectChangeOrderDeliverables',
  SHERPA_PROJECT_TEMPLATES = 'sherpaProjectTemplates',
  SHERPA_PROJECT_TEMPLATE_DETAIL = 'sherpaProjectTemplateDetail',
  SHERPA_PROJECT_TEMPLATE_IMPORT = 'sherpaProjectTemplateImport',

  // Rover routes
  ROVER_ROOT = 'roverRoot',
  ROVER_OVERVIEW = 'roverOverview',
  ROVER_OPPORTUNITIES = 'roverOpportunities',
  ROVER_OPPORTUNITIES_NEW = 'roverOpportunitiesNew',
  ROVER_OPPORTUNITY_DETAIL = 'roverOpportunityDetail',
  ROVER_CLIENT_APPLICATIONS = 'roverClientApplications',
  ROVER_CLIENT_APPLICATIONS_DASHBOARD = 'roverClientApplicationsDashboard',
  ROVER_CLIENT_APPLICATIONS_LIST = 'roverClientApplicationsList',
  ROVER_CLIENT_APPLICATION_DETAIL = 'roverClientApplicationDetail',
  ROVER_PARTNER_PROPOSALS = 'roverPartnerProposals',
  ROVER_PARTNER_PROPOSALS_DASHBOARD = 'roverPartnerProposalsDashboard',
  ROVER_PARTNER_PROPOSALS_LIST = 'roverPartnerProposalsList',
  ROVER_PARTNER_PROPOSAL_DETAIL = 'roverPartnerProposalDetail',
  ROVER_APPLICATION_FORMS = 'roverApplicationForms',
  ROVER_APPLICATION_FORM_DETAIL = 'roverApplicationFormDetail',
  ROVER_APPLICATION_FORM_DASHBOARD = 'roverApplicationFormDashboard',
  ROVER_APPLICATION_FORM_REPORTS = 'roverApplicationFormReports',
  ROVER_APPLICATION_FORM_REPORT_DETAIL = 'roverApplicationFormReportDetail',
  ROVER_APPLICATION_FORM_REPORT_SETTINGS = 'roverApplicationFormReportSettings',
  ROVER_APPLICATION_FORM_APPLICATIONS = 'roverApplicationFormApplications',
  ROVER_APPLICATION_FORM_APPLICATION_DETAIL = 'roverApplicationFormApplicationDetail',
  ROVER_APPLICATION_FORM_APPLICATION_DUE_DILIGENCE = 'roverApplicationFormApplicationDueDiligence',
  ROVER_APPLICATION_FORM_APPLICATION_TASK = 'roverApplicationFormApplicationTask',
  ROVER_APPLICATION_FORM_APPLICATION_DELIVERABLE = 'roverApplicationFormApplicationDeliverable',
  ROVER_APPLICATION_FORM_APPLICATION_MILESTONE = 'roverApplicationFormApplicationMilestone',
  ROVER_APPLICATION_FORM_RESOURCES = 'roverApplicationFormResources',
  ROVER_APPLICATION_FORM_SETTINGS = 'roverApplicationFormSettings',
  ROVER_APPLICATION_FORM_IMPORT = 'roverApplicationFormImport',

  // Lottery routes
  LOTTERY_ROOT = 'lotteryRoot',
  LOTTERY_LIST = 'lotteryList',
  LOTTERY_CREATE = 'lotteryCreate',
  LOTTERY_CREATE_DRAW = 'lotteryCreateDraw',
  LOTTERY_DETAIL = 'lotteryDetail',
  LOTTERY_DRAW_DETAIL = 'lotteryDrawDetail',
  LOTTERY_NOMINEE_APPLICATIONS = 'lotteryNomineeApplications',
  LOTTERY_DRAWS = 'lotteryDraws',

  // Administration routes (stays in /app/)
  ADMIN_ROOT = 'adminRoot',
  ADMIN_USERS = 'adminUsers',
  ADMIN_USER_DETAIL = 'adminUserDetail',
  ADMIN_ORG_TITLES = 'adminOrgTitles',
  ADMIN_ROLES = 'adminRoles',
  ADMIN_CATEGORIES = 'adminCategories',
  ADMIN_WIDGETS = 'adminWidgets',
  ADMIN_GET_STARTED = 'adminGetStarted',

  // Settings routes
  SETTINGS_ROOT = 'settingsRoot',
  SETTINGS_PROFILE = 'settingsProfile',
  SETTINGS_PASSWORD = 'settingsPassword',
  SETTINGS_NOTIFICATIONS = 'settingsNotifications',
  SETTINGS_COMPANY = 'settingsCompany',

  // Vendor routes
  VENDOR = 'vendor',

  // Learn routes
  LEARN = 'learn',

  // Sabacus routes
  SABACUS = 'sabacus',

  // OS routes (sageAdmin only)
  OS_CLIENTS = 'osClients',
  OS_CLIENT_DETAIL = 'osClientDetail',
  OS_SALES_ROOT = 'osSalesRoot',
  OS_SALES = 'osSales',
  OS_PROJECT_TEMPLATES = 'osProjectTemplates',
  OS_PROJECT_TEMPLATE_DETAIL = 'osProjectTemplateDetail',
  OS_PROJECT_TEMPLATE_IMPORT = 'osProjectTemplateImport',
  OS_GOVERNMENT_INTAKE = 'osGovernmentIntake',
  OS_GOVERNMENT_INTAKE_DETAIL = 'osGovernmentIntakeDetails',

  //Government Intake Routes (Public)
  GOVERNMENT_INTAKE_FORM = 'governmentIntake',
  GOVERNMENT_INTAKE_FORM_STEP = 'governmentIntakeFormStep',

  // SME Onboarding routes (public)
  SME_ONBOARDING_START = 'smeOnboardingStart',
  SME_ONBOARDING_EMAIL_SENT = 'smeOnboardingEmailSent',
  SME_ONBOARDING_VERIFY = 'smeOnboardingVerify',
  SME_ONBOARDING_LANDING = 'smeOnboardingLanding',
  SME_ONBOARDING_ORG_REGISTER = 'smeOnboardingOrgRegister',
  SME_ONBOARDING_ORG_ASSESSMENT = 'smeOnboardingOrgAssessment',
  SME_ONBOARDING_ORG_PRICING = 'smeOnboardingOrgPricing',
  SME_ONBOARDING_ORG_REVIEW = 'smeOnboardingOrgReview',
  SME_ONBOARDING_ORG_COMPLETE = 'smeOnboardingOrgComplete',
  SME_ONBOARDING_SINGLE_REGISTER = 'smeOnboardingSingleRegister',
  SME_ONBOARDING_SINGLE_PRICING = 'smeOnboardingSinglePricing',
  SME_ONBOARDING_SINGLE_REVIEW = 'smeOnboardingSingleReview',
  SME_ONBOARDING_SINGLE_COMPLETE = 'smeOnboardingSingleComplete',
  SME_ONBOARDING_SINGLE_TRAINING = 'smeOnboardingSingleTraining',
}

// ============================================================================
// Route Definition Types
// ============================================================================

type RouteDefinition = {
  path: string;
  params?: string[];
};

// ============================================================================
// Route Definitions
// ============================================================================

export const ROUTE_DEFINITIONS: Record<AppRoutes, RouteDefinition> = {
  // Static routes
  [AppRoutes.HOME]: { path: '/' },
  [AppRoutes.LOGOUT]: { path: '/logout' },
  [AppRoutes.SIGNIN]: { path: '/auth/sign_in' },
  [AppRoutes.IDENTITY_AUTH]: { path: '/auth/identity_auth' },
  [AppRoutes.UNAUTHORIZED]: { path: '/access/unauthorized' },
  [AppRoutes.RESET_PASSWORD]: { path: '/reset-password' },
  [AppRoutes.USER_ONBOARD]: { path: '/user-onboard' },
  [AppRoutes.CLIENT_ONBOARD]: { path: '/client-onboard' },
  [AppRoutes.MEMBER_ONBOARD]: { path: '/member-onboard' },
  [AppRoutes.APPLICATIONS]: { path: '/applications' },
  [AppRoutes.MARYLAND_SHERPA]: { path: '/app/marylandSherpa' },
  [AppRoutes.LOTTERY_NOMINEE]: { path: '/lottery/:id/nominee', params: ['id'] },
  [AppRoutes.MKE_ERA_PUBLIC]: { path: '/MKE-ERA/:applicationId', params: ['applicationId'] },
  [AppRoutes.STP_DEMO]: { path: '/stpdemo' },

  // Index routes - Dashboard
  [AppRoutes.APP_COMPANY]: { path: '/terminal/company' },
  [AppRoutes.APP_DASHBOARD]: { path: '/terminal/dashboard' },
  [AppRoutes.APP_TASKS]: { path: '/terminal/dashboard/tasks' },
  [AppRoutes.APP_TASK_DETAIL]: { path: '/terminal/dashboard/tasks/:taskId', params: ['taskId'] },
  [AppRoutes.APP_DELIVERABLES]: { path: '/terminal/dashboard/deliverables' },
  [AppRoutes.APP_DELIVERABLE_DETAIL]: { path: '/terminal/dashboard/deliverables/:deliverableId', params: ['deliverableId'] },
  [AppRoutes.APP_MILESTONES]: { path: '/terminal/dashboard/milestones' },
  [AppRoutes.APP_MILESTONE_DETAIL]: { path: '/terminal/dashboard/milestones/:milestoneId', params: ['milestoneId'] },
  [AppRoutes.APP_APPROVALS]: { path: '/terminal/dashboard/approvals' },
  [AppRoutes.APP_APPROVAL_DETAIL]: { path: '/terminal/dashboard/approvals/:approvalId', params: ['approvalId'] },

  // Sherpa routes
  [AppRoutes.SHERPA_ROOT]: { path: '/terminal/sherpa' },
  [AppRoutes.SHERPA_DASHBOARD]: { path: '/terminal/sherpa/dashboard' },
  [AppRoutes.SHERPA_PROJECTS]: { path: '/terminal/sherpa/projects' },
  [AppRoutes.SHERPA_PROJECTS_NEW]: { path: '/terminal/sherpa/projects/new' },
  [AppRoutes.SHERPA_PROJECTS_IMPORT]: { path: '/terminal/sherpa/projects/import' },
  [AppRoutes.SHERPA_PROJECTS_REPORTS]: { path: '/terminal/sherpa/projects/reports' },
  [AppRoutes.SHERPA_PROJECTS_REPORT_DETAIL]: { path: '/terminal/sherpa/projects/reports/:reportId', params: ['reportId'] },
  [AppRoutes.SHERPA_PROJECTS_REPORT_SETTINGS]: { path: '/terminal/sherpa/projects/reports/:reportId/settings', params: ['reportId'] },
  [AppRoutes.SHERPA_PROJECT_DETAIL]: { path: '/terminal/sherpa/projects/:projectId', params: ['projectId'] },
  [AppRoutes.SHERPA_PROJECT_REPORTS]: { path: '/terminal/sherpa/projects/:projectId/reports', params: ['projectId'] },
  [AppRoutes.SHERPA_PROJECT_REPORT_DETAIL]: {
    path: '/terminal/sherpa/projects/:projectId/reports/:reportId',
    params: ['projectId', 'reportId'],
  },
  [AppRoutes.SHERPA_PROJECT_REPORT_SETTINGS]: {
    path: '/terminal/sherpa/projects/:projectId/reports/:reportId/settings',
    params: ['projectId', 'reportId'],
  },
  [AppRoutes.SHERPA_PROJECT_AUDIT]: { path: '/terminal/sherpa/projects/:projectId/audit', params: ['projectId'] },
  [AppRoutes.SHERPA_PROJECT_IDMS]: { path: '/terminal/sherpa/projects/:projectId/idms', params: ['projectId'] },
  [AppRoutes.SHERPA_PROJECT_IDMS_DRAW]: { path: '/terminal/sherpa/projects/:projectId/idms/:drawId', params: ['projectId', 'drawId'] },
  [AppRoutes.SHERPA_PROJECT_IDMS_DRAW_DOCUMENT]: {
    path: '/terminal/sherpa/projects/:projectId/idms/:drawId/document',
    params: ['projectId', 'drawId'],
  },
  [AppRoutes.SHERPA_PROJECT_IDMS_DRAW_DOCUMENT_DETAIL]: {
    path: '/terminal/sherpa/projects/:projectId/idms/:drawId/document/:documentId',
    params: ['projectId', 'drawId', 'documentId'],
  },
  [AppRoutes.SHERPA_PROJECT_IDMS_DRAW_INVOICE]: {
    path: '/terminal/sherpa/projects/:projectId/idms/:drawId/invoice/:invoiceId',
    params: ['projectId', 'drawId', 'invoiceId'],
  },
  [AppRoutes.SHERPA_PROJECT_CHANGE_ORDER]: { path: '/terminal/sherpa/projects/:projectId/change-order', params: ['projectId'] },
  [AppRoutes.SHERPA_PROJECT_CHANGE_ORDER_BUDGET]: {
    path: '/terminal/sherpa/projects/:projectId/change-order/budget/:changeOrderId',
    params: ['projectId', 'changeOrderId'],
  },
  [AppRoutes.SHERPA_PROJECT_CHANGE_ORDER_MILESTONES]: {
    path: '/terminal/sherpa/projects/:projectId/change-order/milestones/:changeOrderId',
    params: ['projectId', 'changeOrderId'],
  },
  [AppRoutes.SHERPA_PROJECT_CHANGE_ORDER_DELIVERABLES]: {
    path: '/terminal/sherpa/projects/:projectId/change-order/deliverables/:changeOrderId',
    params: ['projectId', 'changeOrderId'],
  },
  [AppRoutes.SHERPA_PROJECT_TEMPLATES]: { path: '/terminal/sherpa/project-templates' },
  [AppRoutes.SHERPA_PROJECT_TEMPLATE_DETAIL]: { path: '/terminal/sherpa/project-templates/:templateId', params: ['templateId'] },
  [AppRoutes.SHERPA_PROJECT_TEMPLATE_IMPORT]: { path: '/terminal/sherpa/project-templates/:templateId/import', params: ['templateId'] },

  // Rover routes
  [AppRoutes.ROVER_ROOT]: { path: '/terminal/rover' },
  [AppRoutes.ROVER_OVERVIEW]: { path: '/terminal/rover/overView' },
  [AppRoutes.ROVER_OPPORTUNITIES]: { path: '/terminal/rover/opportunities' },
  [AppRoutes.ROVER_OPPORTUNITIES_NEW]: { path: '/terminal/rover/opportunities/new' },
  [AppRoutes.ROVER_OPPORTUNITY_DETAIL]: { path: '/terminal/rover/opportunities/:opportunityId', params: ['opportunityId'] },
  [AppRoutes.ROVER_CLIENT_APPLICATIONS]: { path: '/terminal/rover/client-applications' },
  [AppRoutes.ROVER_CLIENT_APPLICATIONS_DASHBOARD]: { path: '/terminal/rover/client-applications/dashboard' },
  [AppRoutes.ROVER_CLIENT_APPLICATIONS_LIST]: { path: '/terminal/rover/client-applications/list' },
  [AppRoutes.ROVER_CLIENT_APPLICATION_DETAIL]: { path: '/terminal/rover/client-applications/list/:id', params: ['id'] },
  [AppRoutes.ROVER_PARTNER_PROPOSALS]: { path: '/terminal/rover/partner-proposals' },
  [AppRoutes.ROVER_PARTNER_PROPOSALS_DASHBOARD]: { path: '/terminal/rover/partner-proposals/dashboard' },
  [AppRoutes.ROVER_PARTNER_PROPOSALS_LIST]: { path: '/terminal/rover/partner-proposals/list' },
  [AppRoutes.ROVER_PARTNER_PROPOSAL_DETAIL]: { path: '/terminal/rover/partner-proposals/list/:id', params: ['id'] },
  [AppRoutes.ROVER_APPLICATION_FORMS]: { path: '/terminal/rover/application_forms' },
  [AppRoutes.ROVER_APPLICATION_FORM_DETAIL]: {
    path: '/terminal/rover/application_forms/:applicationFormId',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_DASHBOARD]: {
    path: '/terminal/rover/application_forms/:applicationFormId/dashboard',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_REPORTS]: {
    path: '/terminal/rover/application_forms/:applicationFormId/reports',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_REPORT_DETAIL]: {
    path: '/terminal/rover/application_forms/:applicationFormId/reports/:reportId',
    params: ['applicationFormId', 'reportId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_REPORT_SETTINGS]: {
    path: '/terminal/rover/application_forms/:applicationFormId/reports/:reportId/settings',
    params: ['applicationFormId', 'reportId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATIONS]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_DETAIL]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications/:applicationId',
    params: ['applicationFormId', 'applicationId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_DUE_DILIGENCE]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications/:applicationId/due_diligence',
    params: ['applicationFormId', 'applicationId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_TASK]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications/:applicationId/due_diligence/task/:taskId',
    params: ['applicationFormId', 'applicationId', 'taskId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_DELIVERABLE]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications/:applicationId/due_diligence/deliverable/:deliverableId',
    params: ['applicationFormId', 'applicationId', 'deliverableId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_MILESTONE]: {
    path: '/terminal/rover/application_forms/:applicationFormId/applications/:applicationId/due_diligence/milestone/:milestoneId',
    params: ['applicationFormId', 'applicationId', 'milestoneId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_RESOURCES]: {
    path: '/terminal/rover/application_forms/:applicationFormId/resources',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_SETTINGS]: {
    path: '/terminal/rover/application_forms/:applicationFormId/settings',
    params: ['applicationFormId'],
  },
  [AppRoutes.ROVER_APPLICATION_FORM_IMPORT]: {
    path: '/terminal/rover/application_forms/:applicationFormId/import_applications',
    params: ['applicationFormId'],
  },

  // Lottery routes
  [AppRoutes.LOTTERY_ROOT]: { path: '/terminal/lottery' },
  [AppRoutes.LOTTERY_LIST]: { path: '/terminal/lottery' },
  [AppRoutes.LOTTERY_CREATE]: { path: '/terminal/lottery/create' },
  [AppRoutes.LOTTERY_CREATE_DRAW]: { path: '/terminal/lottery/create-draw' },
  [AppRoutes.LOTTERY_DETAIL]: { path: '/terminal/lottery/:lotteryId', params: ['lotteryId'] },
  [AppRoutes.LOTTERY_DRAW_DETAIL]: { path: '/terminal/lottery/:lotteryId/draws/:drawId', params: ['lotteryId', 'drawId'] },
  [AppRoutes.LOTTERY_NOMINEE_APPLICATIONS]: { path: '/terminal/lottery/:lotteryId/nominee-applications', params: ['lotteryId'] },
  [AppRoutes.LOTTERY_DRAWS]: { path: '/terminal/lottery/draws' },

  // Administration routes (stays in /app/)
  [AppRoutes.ADMIN_ROOT]: { path: '/terminal/administration' },
  [AppRoutes.ADMIN_USERS]: { path: '/terminal/administration/users' },
  [AppRoutes.ADMIN_USER_DETAIL]: { path: '/terminal/administration/users/:userId', params: ['userId'] },
  [AppRoutes.ADMIN_ORG_TITLES]: { path: '/terminal/administration/organizational-titles' },
  [AppRoutes.ADMIN_ROLES]: { path: '/terminal/administration/roles-and-permissions' },
  [AppRoutes.ADMIN_CATEGORIES]: { path: '/terminal/administration/category-management' },
  [AppRoutes.ADMIN_WIDGETS]: { path: '/terminal/administration/widgets-management' },
  [AppRoutes.ADMIN_GET_STARTED]: { path: '/terminal/administration/get-started' },

  // Settings routes
  [AppRoutes.SETTINGS_ROOT]: { path: '/terminal/settings' },
  [AppRoutes.SETTINGS_PROFILE]: { path: '/terminal/settings/profile' },
  [AppRoutes.SETTINGS_PASSWORD]: { path: '/terminal/settings/password' },
  [AppRoutes.SETTINGS_NOTIFICATIONS]: { path: '/terminal/settings/notifications' },
  [AppRoutes.SETTINGS_COMPANY]: { path: '/terminal/settings/company-profile' },

  // Vendor routes
  [AppRoutes.VENDOR]: { path: '/terminal/vendor' },

  // Learn routes
  [AppRoutes.LEARN]: { path: '/terminal/learn' },

  // Sabacus routes
  [AppRoutes.SABACUS]: { path: '/terminal/sabacus' },

  // OS routes (sageAdmin only)
  [AppRoutes.OS_CLIENTS]: { path: '/os/clients' },
  [AppRoutes.OS_CLIENT_DETAIL]: { path: '/os/clients/:clientId', params: ['clientId'] },
  [AppRoutes.OS_SALES_ROOT]: { path: '/os/sales-marketting' },
  [AppRoutes.OS_SALES]: { path: '/os/sales-marketting/sales' },
  [AppRoutes.OS_PROJECT_TEMPLATES]: { path: '/os/project-templates' },
  [AppRoutes.OS_PROJECT_TEMPLATE_DETAIL]: { path: '/os/project-templates/:templateId', params: ['templateId'] },
  [AppRoutes.OS_PROJECT_TEMPLATE_IMPORT]: { path: '/os/project-templates/:templateId/import', params: ['templateId'] },
  [AppRoutes.OS_GOVERNMENT_INTAKE]: { path: '/os/sales-marketting/government-intake' },
  [AppRoutes.OS_GOVERNMENT_INTAKE_DETAIL]: { path: '/os/sales-marketting/government-intake/:intakeId', params: ['intakeId'] },

  //Government intake (public)
  [AppRoutes.GOVERNMENT_INTAKE_FORM]: { path: '/government-intake' },
  [AppRoutes.GOVERNMENT_INTAKE_FORM_STEP]: { path: '/government-intake/form' },

  // SME Onboarding routes (public)
  [AppRoutes.SME_ONBOARDING_START]: { path: '/onboarding/start' },
  [AppRoutes.SME_ONBOARDING_EMAIL_SENT]: { path: '/onboarding/email-sent' },
  [AppRoutes.SME_ONBOARDING_VERIFY]: { path: '/onboarding/verify' },
  [AppRoutes.SME_ONBOARDING_LANDING]: { path: '/onboarding/choose' },
  [AppRoutes.SME_ONBOARDING_ORG_REGISTER]: { path: '/onboarding/organization/register' },
  [AppRoutes.SME_ONBOARDING_ORG_ASSESSMENT]: { path: '/onboarding/organization/assessment' },
  [AppRoutes.SME_ONBOARDING_ORG_PRICING]: { path: '/onboarding/organization/pricing' },
  [AppRoutes.SME_ONBOARDING_ORG_REVIEW]: { path: '/onboarding/organization/review' },
  [AppRoutes.SME_ONBOARDING_ORG_COMPLETE]: { path: '/onboarding/organization/complete' },
  [AppRoutes.SME_ONBOARDING_SINGLE_REGISTER]: { path: '/onboarding/single-project/register' },
  [AppRoutes.SME_ONBOARDING_SINGLE_PRICING]: { path: '/onboarding/single-project/pricing' },
  [AppRoutes.SME_ONBOARDING_SINGLE_REVIEW]: { path: '/onboarding/single-project/review' },
  [AppRoutes.SME_ONBOARDING_SINGLE_COMPLETE]: { path: '/onboarding/single-project/complete' },
  [AppRoutes.SME_ONBOARDING_SINGLE_TRAINING]: { path: '/onboarding/single-project/training' },
};

// ============================================================================
// buildRoute Function - Type-safe route builder
// ============================================================================

/**
 * Type-safe route builder function
 *
 * @example
 * // Routes without parameters
 * buildRoute(AppRoutes.HOME) // returns '/'
 * buildRoute(AppRoutes.SHERPA_DASHBOARD) // returns '/terminal/sherpa/dashboard'
 *
 * @example
 * // Routes with parameters
 * buildRoute(AppRoutes.APP_TASK_DETAIL, { taskId: '123' })
 * // returns '/terminal/dashboard/tasks/123'
 *
 * buildRoute(AppRoutes.SHERPA_PROJECT_DETAIL, { projectId: 'abc-123' })
 * // returns '/terminal/sherpa/projects/abc-123'
 *
 * @example
 * // Runtime errors if required params are missing
 * buildRoute(AppRoutes.APP_TASK_DETAIL) // ❌ Runtime error
 * buildRoute(AppRoutes.APP_TASK_DETAIL, { wrongParam: '123' }) // ❌ Runtime error
 */
export function buildRoute(routeKey: AppRoutes, params?: Record<string, string | number>): string {
  const definition = ROUTE_DEFINITIONS[routeKey];
  let path = definition.path;

  // Runtime parameter validation
  if (definition.params && definition.params.length > 0) {
    if (!params) {
      throw new Error(`Route ${routeKey} requires params: ${definition.params.join(', ')}`);
    }

    for (const param of definition.params) {
      if (!(param in params)) {
        throw new Error(`Missing required parameter "${param}" for route ${routeKey}`);
      }
      path = path.replace(`:${param}`, String(params[param]));
    }
  }

  return path;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the route definition for a given route key
 */
export function getRouteDefinition(routeKey: AppRoutes): RouteDefinition {
  return ROUTE_DEFINITIONS[routeKey];
}

/**
 * Check if a route requires parameters
 */
export function routeHasParams(routeKey: AppRoutes): boolean {
  const definition = ROUTE_DEFINITIONS[routeKey];
  return !!definition.params && definition.params.length > 0;
}

export function normalizeTenantRoute(path: string): string {
  if (path === '/app') {
    return '/terminal';
  }

  if (path.startsWith('/app/')) {
    return `/terminal/${path.slice('/app/'.length)}`;
  }

  return path;
}

/**
 * Get the required parameters for a route
 */
export function getRouteParams(routeKey: AppRoutes): string[] | undefined {
  return ROUTE_DEFINITIONS[routeKey].params;
}
